import { useEffect, useState } from 'react';
import { isBrowserOffline } from '@bloomays-lib/utils.shared';

/**
 * Reactive online/offline flag driven by the standard browser events.
 *
 * `navigator.onLine` is not perfect (a `true` value only means the browser
 * has *some* network route, not that our GraphQL backend is reachable), but
 * combined with the Apollo retry link + monitoring filter it is enough to
 * surface a discreet offline indicator to the user and avoid flooding Sentry
 * on real network drops.
 */
export const useOnlineStatus = (): boolean => {
  const [isOnline, setIsOnline] = useState<boolean>(() => !isBrowserOffline());

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
};
